import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { SkeletonMachinesCustomers } from "../components/SkeletonLoader";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import FadeIn from "../components/FadeIn";
import { Link } from "react-router-dom";
import { Plus, Search, Copy, Check, X, Loader2, Filter, Users, Mail, Sparkles } from "lucide-react";
import Modal from "../components/Modal";
import AiDocumentIntake from "../components/ai/AiDocumentIntake";
import ContactPersonList from "../components/ContactPersonList";

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useDelayedLoading(loading, 250);
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showEmails, setShowEmails] = useState(false);
  const [showAiIntake, setShowAiIntake] = useState(false);
  const [aresLoading, setAresLoading] = useState(false);
  const [aresError, setAresError] = useState("");
  const pendingContactsRef = useRef([]);
  const [form, setForm] = useState({ company_name: "", proxy_name: "", phone: "", email: "", street: "", city: "", postal_code: "", ico: "", dic: "", notes: "" });

  const statusLabels = { nahled: "Náhled", neobjednano: "Neobjednáno", objednano: "Objednáno", ve_vyrobe: "Ve výrobě", pripraveno: "Připraveno", doruceno: "Doručeno" };
  const statusColors = { nahled: { bg: "#FFF8E1", color: "#FF8F00" }, neobjednano: { bg: "#F5F5F5", color: "#666" }, objednano: { bg: "#E3F2FD", color: "#1976D2" }, ve_vyrobe: { bg: "#FFF3E0", color: "#F57C00" }, pripraveno: { bg: "#F3E5F5", color: "#7B1FA2" }, doruceno: { bg: "#E8F5E9", color: "#388E3C" } };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [custRes, machRes] = await Promise.all([fetch("/api/customers", { credentials: "include" }), fetch("/api/machines", { credentials: "include" })]);
      if (custRes.ok) { const json = await custRes.json(); setCustomers(json.data || []); }
      if (machRes.ok) { const json = await machRes.json(); setMachines(json.data || []); }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const getMachinesForCustomer = (customerId) => machines.filter(m => m.customer_id === customerId);
  const handleCopy = (text, id) => { setCopied(id); setTimeout(() => setCopied(null), 1500); (async () => { try { await navigator.clipboard.writeText(text); } catch (e) { try { const ta = document.createElement('textarea'); ta.value = text; ta.style.cssText = 'position:fixed;opacity:0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); } catch (e2) {} } })(); };
  
  const filteredCustomers = customers.filter(c => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      c.company_name?.toLowerCase().includes(searchLower) ||
      c.ico?.includes(search) ||
      c.proxy_name?.toLowerCase().includes(searchLower) ||
      c.city?.toLowerCase().includes(searchLower) ||
      c.email?.toLowerCase().includes(searchLower) ||
      c.location?.toLowerCase().includes(searchLower)
    );
  });

  const clearSearch = () => {
    setSearch('');
  };

    const lookupAres = async () => {
    const ico = form.ico.replace(/\s/g, "");
    if (!ico || ico.length !== 8) { setAresError("IČO musí mít 8 číslic"); return; }
    setAresLoading(true);
    setAresError("");
    try {
      const res = await fetch("/api/customers/ares/" + ico, { credentials: "include" });
      const json = await res.json();
      if (res.ok && json.data) {
        setForm(prev => ({ ...prev, ...json.data }));
      } else {
        setAresError(json.message || "Subjekt nenalezen");
      }
    } catch (err) {
      setAresError("Nepodařilo se připojit k ARES");
    }
    setAresLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAresError(""); // Clear previous errors
    try {
      const res = await fetch("/api/customers", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(form) });
      if (res.ok) {
        const data = await res.json();
        const newCustomerId = data.data?.id || data.id;
        // POST pending contacts if any
        if (newCustomerId && pendingContactsRef.current.length > 0) {
          for (const contact of pendingContactsRef.current) {
            const { _pending, _tempId, ...contactData } = contact;
            await fetch(`/api/customers/${newCustomerId}/contacts`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(contactData) });
          }
          pendingContactsRef.current = [];
        }
        setShowForm(false); 
        setForm({ company_name: "", proxy_name: "", phone: "", email: "", street: "", city: "", postal_code: "", ico: "", dic: "", notes: "" }); 
        fetchData(); 
      } else {
        const data = await res.json();
        setAresError(data.error || "Nepodařilo se vytvořit zákazníka");
      }
    } catch (err) { 
      console.error(err);
      setAresError("Chyba při komunikaci se serverem");
    }
  };

  const inputStyle = { width: "100%", padding: "10px 14px", border: "1px solid #E0E0E0", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: "13px", fontWeight: 500, color: "#333", marginBottom: "6px" };

  if (showLoading) return <SkeletonMachinesCustomers />;

  return (
    <FadeIn show={!showLoading}>
    <div style={{ padding: "24px" }}>
      <AiDocumentIntake open={showAiIntake} onClose={() => setShowAiIntake(false)} onSaved={() => fetchData()} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#333", margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
          <Users color="#E53935" /> Zákazníci
        </h1>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button onClick={() => setShowAiIntake(true)} title="Nahrajte kupní smlouvu – AI založí zákazníka, trenažéry a přiloží smlouvu" style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 16px", backgroundColor: "#F3E5F5", color: "#7B1FA2", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>
            <Sparkles size={18} /> Ze smlouvy (AI)
          </button>
          <button onClick={() => setShowEmails(true)} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 16px", backgroundColor: "#FFF", color: "#333", border: "1px solid #E0E0E0", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>
            <Mail size={18} /> Emaily klientů
          </button>
          <button onClick={() => { setShowForm(true); setAresError(""); pendingContactsRef.current = []; }} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 16px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>
            <Plus size={18} /> Nový zákazník
          </button>
        </div>
      </div>

      <div style={{ backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #E0E0E0', padding: '20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Filter size={20} color="#E53935" />
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#333', margin: 0 }}>Vyhledávání zákazníků</h3>
          {search && (
            <button
              onClick={clearSearch}
              style={{
                marginLeft: 'auto',
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: '#F5F5F5',
                color: '#666',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Vymazat
            </button>
          )}
        </div>
        
        <div style={{ position: "relative" }}>
          <Search style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#999" }} size={20} />
          <input 
            type="text" 
            placeholder="Název firmy, IČO, kontaktní osoba, město, email..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            style={{ 
              width: "100%", 
              padding: "12px 12px 12px 44px", 
              border: "1px solid #E0E0E0", 
              borderRadius: "8px", 
              fontSize: "14px", 
              boxSizing: "border-box" 
            }} 
          />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
          Zobrazeno {filteredCustomers.length} z {customers.length} zákazníků
        </p>
      </div>

      {filteredCustomers.length === 0 ? (
        <div style={{ 
          textAlign: "center", 
          padding: "48px", 
          color: "#999",
          backgroundColor: "#FFF",
          borderRadius: "12px",
          border: "1px solid #E0E0E0"
        }}>
          {search ? 'Žádní zákazníci nevyhovují vyhledávání' : 'Žádní zákazníci'}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {filteredCustomers.map((customer) => {
            const custMachines = getMachinesForCustomer(customer.id);
            return (
              <div key={customer.id} style={{ 
                backgroundColor: "#FFF", 
                borderRadius: "12px", 
                border: "1px solid #E0E0E0", 
                padding: "20px", 
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.transform = "translateY(-2px)"; 
                e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)"; 
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.transform = "translateY(0)"; 
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)"; 
              }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
                  <div>
                    <Link to={"/customers/" + customer.id} style={{ fontWeight: 600, fontSize: "15px", color: "#333", textDecoration: "none", display: "block", marginBottom: "4px" }}>
                      {customer.company_name}
                    </Link>
                    {customer.ico && (
                      <p style={{ fontSize: "12px", color: "#999", margin: "0 0 6px" }}>IČO: {customer.ico}</p>
                    )}
                    <div style={{ fontSize: "14px", color: customer.proxy_name ? "#333" : "#999", marginBottom: "6px" }}>
                      {customer.proxy_name || "Zastupitel - doplnit"}
                    </div>
                    <div style={{ marginBottom: "6px" }}>
                      {customer.phone ? (
                        <button onClick={() => handleCopy(customer.phone, "phone-" + customer.id)} style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#333", cursor: "pointer", fontSize: "14px", padding: 0 }}>
                          {customer.phone} {copied === "phone-" + customer.id ? <Check size={14} color="#4CAF50" /> : <Copy size={14} color="#999" />}
                        </button>
                      ) : <span style={{ fontSize: "14px", color: "#999" }}>Tel. číslo - doplnit</span>}
                    </div>
                    <div>
                      {customer.email ? (
                        <button onClick={() => handleCopy(customer.email, "email-" + customer.id)} style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#333", cursor: "pointer", fontSize: "14px", padding: 0 }}>
                          {customer.email} {copied === "email-" + customer.id ? <Check size={14} color="#4CAF50" /> : <Copy size={14} color="#999" />}
                        </button>
                      ) : <span style={{ fontSize: "14px", color: "#999" }}>Email - doplnit</span>}
                    </div>
                  </div>
                  <div>
                    <h4 style={{ fontSize: "13px", fontWeight: 600, color: "#666", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Trenažéry ({custMachines.length})
                    </h4>
                    {custMachines.length === 0 ? (
                      <span style={{ color: "#999", fontSize: "14px" }}>Žádné trenažéry</span>
                    ) : (
                      custMachines.map(m => (
                        <Link key={m.id} to={"/machines/" + m.id} style={{ display: "block", fontSize: "14px", color: "#333", textDecoration: "none", marginBottom: "4px", padding: "4px 0" }}>
                          Autom {m.model_type} : {m.serial_number}
                        </Link>
                      ))
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {custMachines.length > 0 && (
                      <h4 style={{ fontSize: "13px", fontWeight: 600, color: "#666", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Stavy
                      </h4>
                    )}
                    {custMachines.map(m => {
                      const sc = statusColors[m.status] || statusColors.neobjednano;
                      return (
                        <div key={m.id} style={{ marginBottom: "4px" }}>
                          <span style={{ 
                            display: "inline-block", 
                            padding: "4px 10px", 
                            borderRadius: "6px", 
                            fontSize: "12px", 
                            backgroundColor: sc.bg, 
                            color: sc.color,
                            fontWeight: 500
                          }}>
                            {statusLabels[m.status] || "Neobjednáno"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal show={showForm} onClose={() => { setShowForm(false); pendingContactsRef.current = []; }} zIndex={9999}>
          <div style={{ backgroundColor: "#FFF", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "500px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 25px rgba(0,0,0,0.15)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}>Nový zákazník</h2>
              <button onClick={() => { setShowForm(false); pendingContactsRef.current = []; }} style={{ padding: "4px", background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "16px" }}><label style={labelStyle}>Název autoškoly *</label><input type="text" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} style={inputStyle} placeholder="Např. Autoškola Novák" /></div>
              <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", marginBottom: "16px" }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>IČO</label><input type="text" value={form.ico} onChange={(e) => setForm({ ...form, ico: e.target.value })} style={inputStyle} placeholder="12345678" /></div>
                <button type="button" onClick={lookupAres} disabled={aresLoading} style={{ padding: "10px 14px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: aresLoading ? "not-allowed" : "pointer", opacity: aresLoading ? 0.7 : 1, display: "flex", alignItems: "center", justifyContent: "center", height: "42px" }} title="Načíst z ARES">
                  {aresLoading ? <Loader2 size={20} /> : <Search size={20} />}
                </button>
                <div style={{ flex: 1 }}><label style={labelStyle}>DIČ</label><input type="text" value={form.dic} onChange={(e) => setForm({ ...form, dic: e.target.value })} style={inputStyle} placeholder="CZ12345678" /></div>
              </div>
              {aresError && <p style={{ color: "#E53935", fontSize: "13px", margin: "-10px 0 16px" }}>{aresError}</p>}
              <div style={{ marginBottom: "16px" }}><label style={labelStyle}>Ulice</label><input type="text" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} style={inputStyle} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
                <div><label style={labelStyle}>Město</label><input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>PSČ</label><input type="text" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} style={inputStyle} /></div>
              </div>
              <div style={{ marginBottom: "16px" }}><label style={labelStyle}>Zastupitel</label><input type="text" value={form.proxy_name} onChange={(e) => setForm({ ...form, proxy_name: e.target.value })} style={inputStyle} placeholder="Jan Novák" /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
                <div><label style={labelStyle}>Telefon</label><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={inputStyle} placeholder="+420 xxx xxx xxx" /></div>
                <div><label style={labelStyle}>Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} placeholder="info@autoskola.cz" /></div>
              </div>
              <div style={{ marginBottom: "16px" }}>
                <ContactPersonList
                  customerId={null}
                  onPendingContactsChange={(contacts) => { pendingContactsRef.current = contacts; }}
                />
              </div>
              <div style={{ marginBottom: "20px" }}><label style={labelStyle}>Poznámky</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} style={{ ...inputStyle, resize: "vertical" }} /></div>
              <div style={{ display: "flex", gap: "12px" }}>
                <button type="button" onClick={() => { setShowForm(false); pendingContactsRef.current = []; }} style={{ flex: 1, padding: "12px", border: "1px solid #E0E0E0", borderRadius: "8px", backgroundColor: "#FFF", cursor: "pointer", fontSize: "14px" }}>Zrušit</button>
                <button type="submit" style={{ flex: 1, padding: "12px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>Vytvořit</button>
              </div>
            </form>
          </div>
      </Modal>
    </div>
      {showEmails && createPortal(
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999 }} onClick={() => setShowEmails(false)}>
          <div style={{ background: "#FFF", borderRadius: "12px", padding: "24px", width: "90%", maxWidth: "560px", maxHeight: "80vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 700, margin: 0 }}>📧 Emaily klientů</h2>
              <button onClick={() => setShowEmails(false)} style={{ padding: "4px", background: "none", border: "none", cursor: "pointer" }}><X size={20} color="#999" /></button>
            </div>
            {(() => {
              const emails = customers.filter(c => c.email && c.email.trim()).map(c => c.email.trim());
              const uniqueEmails = [...new Set(emails)];
              if (uniqueEmails.length === 0) {
                return <p style={{ color: "#999", textAlign: "center", padding: "20px" }}>Žádné emaily nenalezeny</p>;
              }
              const allEmails = uniqueEmails.join(", ");
              return (
                <div>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                    <button onClick={() => { navigator.clipboard.writeText(allEmails); setCopied("emails"); setTimeout(() => setCopied(null), 2000); }} style={{ padding: "8px 16px", borderRadius: "6px", border: "none", background: "#E53935", color: "#FFF", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                      {copied === "emails" ? "✓ Zkopírováno!" : "Kopírovat všechny"}
                    </button>
                    <span style={{ fontSize: "13px", color: "#666", alignSelf: "center" }}>Celkem: {uniqueEmails.length} emailů</span>
                  </div>
                  <div style={{ padding: "12px", backgroundColor: "#F9F9F9", borderRadius: "8px", fontSize: "13px", color: "#333", wordBreak: "break-all", lineHeight: "1.6" }}>
                    {allEmails}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>,
        document.body
      )}
    </FadeIn>
  );
};

export default Customers;